import type { RequestScenario } from '@/types/admin'
import { ApiError } from '@/utils/errors'
import { simulationState } from './simulation'

type UnauthorizedHandler = () => void
const unauthorizedHandlers = new Set<UnauthorizedHandler>()

export function onUnauthorized(handler: UnauthorizedHandler): () => void {
  unauthorizedHandlers.add(handler)
  return () => unauthorizedHandlers.delete(handler)
}

export interface RequestOptions {
  scenario?: RequestScenario
  signal?: AbortSignal
  delay?: number
  write?: boolean
  /** When true the request respects the global fault-injection selector. */
  useSimulation?: boolean
}

function pickScenario(options: RequestOptions): RequestScenario {
  if (options.scenario) return options.scenario
  if (simulationState.offline) return 'offline'
  return options.useSimulation === false ? 'normal' : simulationState.scenario
}

function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ApiError('cancel'))
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort)
      resolve()
    }, milliseconds)
    const abort = (): void => {
      clearTimeout(timer)
      reject(new ApiError('cancel'))
    }
    signal?.addEventListener('abort', abort, { once: true })
  })
}

/** Typed request boundary that emulates latency and the full failure matrix. */
export async function request<T>(
  loader: () => T | Promise<T>,
  options: RequestOptions = {},
): Promise<T> {
  const scenario = pickScenario(options)
  const baseDelay = options.delay ?? (options.write ? simulationState.writeDelay : simulationState.latency)
  const delay = scenario === 'timeout' ? Math.max(baseDelay, 900) : baseDelay
  await wait(delay, options.signal)

  if (scenario === 'timeout') throw new ApiError('timeout')
  if (scenario === 'cancel') throw new ApiError('cancel')
  if (scenario === 'offline') throw new ApiError('offline')
  if (scenario !== 'normal') {
    if (scenario === '401') unauthorizedHandlers.forEach(handler => handler())
    throw new ApiError(scenario)
  }
  return loader()
}

export function runWithTimeout<T>(promise: Promise<T>, milliseconds: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new ApiError('timeout')), milliseconds)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}
